// Line chart: 2px lines, recessive hairline grid, one y-axis, crosshair + one tooltip listing
// every series, end markers with a surface ring, legend for 2+ series, gaps for "No data" days.
import { useState } from 'react';
import { formatShortDate, formatValue, niceTicks, seriesColor } from '../../lib/format';
import { useSize } from '../../lib/useSize';
import type { RegistryEntry, WidgetComponentProps } from '../types';

interface Series {
  id: string;
  label: string;
  points: { t: string; v: number | null }[];
}

function LineChart({ data, props }: WidgetComponentProps) {
  const [ref, { width, height }] = useSize<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const series: Series[] = data.series ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const n = series[0]?.points.length ?? 0;
  const fmt = (v: number | null, compact = false) => formatValue(v, data.format, { currency: data.currency, compact });
  const legendH = series.length > 1 ? 22 : 0;
  const all = series.flatMap((s) => s.points.map((p) => p.v)).filter((v): v is number => v !== null);
  const bench = props.benchmark as { min: number; max: number } | undefined;
  const maxV = Math.max(0, ...all, bench?.max ?? 0);
  const minV = Math.min(0, ...all);
  const ticks = niceTicks(minV, maxV * 1.05 || 1, 4);
  const yMax = ticks[ticks.length - 1];
  const yMin = ticks[0];
  const labelW = Math.max(...ticks.map((t) => fmt(t, true).length)) * 6.6 + 10;
  const m = { top: 8, right: 14, bottom: 22, left: labelW };
  const w = Math.max(0, width - m.left - m.right);
  const h = Math.max(0, height - legendH - m.top - m.bottom);
  const x = (i: number) => m.left + (n <= 1 ? w / 2 : (i / (n - 1)) * w);
  const y = (v: number) => m.top + h - ((v - yMin) / (yMax - yMin || 1)) * h;
  const step = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(w / 70))));

  // Completed days draw solid; the segment into today's still-accumulating point draws faded,
  // so a partial day never reads as a collapse.
  const path = (s: Series) => {
    let d = '';
    let pen = false;
    s.points.forEach((p, i) => {
      if (p.v === null || p.t === today) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };
  const partialPath = (s: Series) => {
    const i = s.points.findIndex((p) => p.t === today);
    if (i < 1 || s.points[i].v === null || s.points[i - 1].v === null) return '';
    return `M${x(i - 1).toFixed(1)},${y(s.points[i - 1].v!).toFixed(1)}L${x(i).toFixed(1)},${y(s.points[i].v!).toFixed(1)}`;
  };
  const area = (s: Series) => {
    const pts = s.points.map((p, i) => ({ i, v: p.v })).filter((p) => p.v !== null) as { i: number; v: number }[];
    if (pts.length < 2) return '';
    return `M${x(pts[0].i)},${y(Math.max(0, yMin))}` + pts.map((p) => `L${x(p.i)},${y(p.v)}`).join('') + `L${x(pts[pts.length - 1].i)},${y(Math.max(0, yMin))}Z`;
  };

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = e.clientX - rect.left - m.left;
    setHover(Math.max(0, Math.min(n - 1, Math.round((px / (w || 1)) * (n - 1)))));
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') setHover((h0) => Math.min(n - 1, (h0 ?? -1) + 1));
    else if (e.key === 'ArrowLeft') setHover((h0) => Math.max(0, (h0 ?? n) - 1));
    else if (e.key === 'Escape') setHover(null);
  };
  const summary = series.map((s) => {
    const last = [...s.points].reverse().find((p) => p.v !== null);
    return `${s.label}: latest ${fmt(last?.v ?? null)}`;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {series.length > 1 && (
        <div className="legend" style={{ height: legendH - 6 }}>
          {series.map((s, i) => (
            <span className="legend-item" key={s.id}>
              <span className="key-line" style={{ background: seriesColor(s.id, i) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
      <div className="chart" ref={ref}>
        {width > 0 && height > 0 && (
          <svg width={width} height={height - legendH} role="img" aria-label={`${summary.join('; ')}. Use arrow keys to inspect days.`} tabIndex={0} onKeyDown={onKey} onBlur={() => setHover(null)}>
            {bench && (
              <g>
                <rect x={m.left} width={w} y={y(bench.max)} height={Math.max(0, y(bench.min) - y(bench.max))} fill="var(--surface-3)" opacity={0.6} />
                <text x={m.left + w - 4} y={y(bench.max) - 4} textAnchor="end">
                  Benchmark {formatValue(bench.min, data.format)}–{formatValue(bench.max, data.format)}
                </text>
              </g>
            )}
            {ticks.map((t) => (
              <g key={t}>
                <line x1={m.left} x2={m.left + w} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--axis)' : 'var(--grid)'} strokeWidth={1} />
                <text x={m.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="tabular">
                  {fmt(t, true)}
                </text>
              </g>
            ))}
            {series[0]?.points.map((p, i) =>
              (i % step === 0 && (i === n - 1 || n - 1 - i >= step * 0.6)) || i === n - 1 ? (
                <text key={p.t} x={x(i)} y={m.top + h + 15} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
                  {formatShortDate(p.t)}
                </text>
              ) : null,
            )}
            {props.area && series.map((s, i) => <path key={`a${s.id}`} className="area-in" d={area(s)} fill={seriesColor(s.id, i)} opacity={0.1} />)}
            {series.map((s, i) => (
              <g key={s.id}>
                <path className="line-draw" pathLength={1} style={{ ['--si' as any]: i }} d={path(s)} fill="none" stroke={seriesColor(s.id, i)} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                <path d={partialPath(s)} fill="none" stroke={seriesColor(s.id, i)} strokeWidth={2} strokeLinecap="round" opacity={0.35} />
              </g>
            ))}
            {hover === null &&
              series.map((s, i) => {
                const idx = s.points.map((p) => p.v !== null).lastIndexOf(true);
                if (idx < 0) return null;
                // Today's point is still accumulating: hollow marker so a partial day doesn't read as a drop.
                const partial = s.points[idx].t === today;
                return partial ? (
                  <circle key={`e${s.id}`} cx={x(idx)} cy={y(s.points[idx].v!)} r={4} fill="var(--surface-1)" stroke={seriesColor(s.id, i)} strokeWidth={2} />
                ) : (
                  <circle key={`e${s.id}`} cx={x(idx)} cy={y(s.points[idx].v!)} r={4} fill={seriesColor(s.id, i)} stroke="var(--surface-1)" strokeWidth={2} />
                );
              })}
            {hover !== null && (
              <g pointerEvents="none">
                <line x1={x(hover)} x2={x(hover)} y1={m.top} y2={m.top + h} stroke="var(--axis)" strokeWidth={1} />
                {series.map((s, i) =>
                  s.points[hover]?.v !== null && s.points[hover] ? (
                    <circle key={s.id} cx={x(hover)} cy={y(s.points[hover].v!)} r={4} fill={seriesColor(s.id, i)} stroke="var(--surface-1)" strokeWidth={2} />
                  ) : null,
                )}
              </g>
            )}
            <rect x={m.left} y={m.top} width={w} height={h} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
          </svg>
        )}
        {hover !== null && series[0]?.points[hover] && (
          <div className="tooltip" style={{ left: Math.min(x(hover) + 12, width - 170), top: 4 }}>
            <div className="tt-title">
              {formatShortDate(series[0].points[hover].t)}
              {series[0].points[hover].t === today ? ' — today, partial' : ''}
            </div>
            {series.map((s, i) => (
              <div className="tt-row" key={s.id}>
                <span className="key-line" style={{ background: seriesColor(s.id, i) }} />
                <b>{s.points[hover]?.v === null ? 'No data' : fmt(s.points[hover]?.v ?? null)}</b>
                <span className="name">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export const lineChart: RegistryEntry = {
  component: LineChart,
  isEmpty: (d) => !d?.series?.length || d.series.every((s: Series) => s.points.every((p) => p.v === null)),
  toTable: (d) => {
    const s: Series[] = d?.series ?? [];
    if (!s.length) return null;
    return {
      columns: [{ key: 't', label: 'Day', format: 'date' }, ...s.map((x) => ({ key: x.id, label: x.label, format: d.format, currency: d.currency, numeric: true }))],
      rows: s[0].points.map((p, i) => Object.fromEntries([['t', p.t], ...s.map((x) => [x.id, x.points[i]?.v ?? null])])),
    };
  },
};
